/**
 * useHangingProtocolSync — keeps the four phase-dependent viewports in lock-step
 * for the toggles in the Contrast Phase box. Only active when the project's
 * gating mode is `phase_dependent`; for view_dependent (us3d) and ungated
 * projects the slots hold unrelated series and crosslinking would be wrong.
 *
 * Two layers:
 *  1. Pan/zoom sync via a Cornerstone `createZoomPanSynchronizer` — pan and
 *     zoom are cornerstone-camera changes, so cornerstone's bundled
 *     CAMERA_MODIFIED synchronizer propagates them cleanly across viewports.
 *  2. Slice sync via Jotai slot atoms (`slotStateFamily`). The active phase's
 *     current slice is matched to the slice in each other phase whose WORLD
 *     POSITION (signed mm along the slice normal) is nearest — not the same
 *     index, and not the same distance from slice 0. Phases of one study share
 *     a frame of reference, so their per-slice world positions are directly
 *     comparable even when the scans cover different cranio-caudal ranges (an
 *     arterial phase over just the liver vs a portal phase down to the pelvis).
 *     World positions come from study metadata, so slice sync behaves
 *     identically on stack and volume viewports. See `sliceSyncMath.ts`.
 */
import { useEffect, useMemo, useRef } from "react";
import { useAtomValue, useStore } from "jotai";
import { manipulationSyncEnabledAtom, sliceSyncEnabledAtom } from "../atoms/viewportSyncAtoms";
import { availableDiscriminatorsAtom, projectGatingModeAtom } from "../atoms/gatingAtoms";
import { GATING_MODE } from "../types/gating";
import { activeSlotAtom, slotStateFamily } from "../atoms/hangingProtocolAtoms";
import { isSegmentationActiveAtom } from "../atoms/segmentationAtoms";
import {
  hangingProtocolViewportPrefix,
  SLOT_IDS,
  type SlotId,
} from "../utils/hangingProtocolService";
import { getCornerstone, getCornerstoneTools } from "../utils/dicomLoader";
import { SHARED_RENDERING_ENGINE_ID } from "../utils/renderingEngine";
import type { StudyMetadataResponse } from "../utils/dicomMetadataLoader";
import { instanceWorldZ, nearestSliceByWorldZ, sliceNormalFromIop } from "./sliceSyncMath";
import { logger } from "@/utils/logger";

const ZOOM_PAN_SYNC_ID = "hpPhaseZoomPanSync";

/** Pan/zoom sync: thin wrapper over Cornerstone's zoom/pan synchronizer.
 *
 * `syncedSlots` is stable (always `SLOT_IDS`). The seg-panel slot has no
 * cornerstone viewport when it's active, so `isPhaseViewport` naturally
 * filters it out at runtime — no separate skip needed here. (#389)
 */
function useZoomPanSync(enabled: boolean, syncedSlots: readonly SlotId[]): void {
  const slotPrefixes = useMemo(() => syncedSlots.map(hangingProtocolViewportPrefix), [syncedSlots]);
  useEffect(() => {
    let disposed = false;
    const isPhaseViewport = (viewportId: string) =>
      slotPrefixes.some((p) => viewportId.startsWith(p));

    const apply = () => {
      if (disposed) return;
      let cornerstone: ReturnType<typeof getCornerstone>;
      let tools: ReturnType<typeof getCornerstoneTools>;
      try {
        cornerstone = getCornerstone();
        tools = getCornerstoneTools();
      } catch (err) {
        logger.debug("[viewportSync] cornerstone not initialized yet:", err);
        return;
      }

      const engine = cornerstone.getRenderingEngine(SHARED_RENDERING_ENGINE_ID);
      if (!engine) return;

      // Each slot may have multiple viewport entries lingering after a
      // React remount; cornerstone dispatches events under the viewport ID
      // registered for the currently-enabled element, so we must register
      // the same ID via getEnabledElement(element).viewportId.
      type Vp = { id: string; element?: HTMLDivElement | null };
      const all = (engine.getViewports() as Vp[]).filter((vp) => isPhaseViewport(vp.id));
      const liveIds = new Set<string>();
      for (const vp of all) {
        if (!vp.element || !document.body.contains(vp.element)) continue;
        const ee = cornerstone.getEnabledElement(vp.element);
        if (ee?.viewportId) liveIds.add(ee.viewportId);
      }
      const ids = Array.from(liveIds).map((viewportId) => ({
        renderingEngineId: SHARED_RENDERING_ENGINE_ID,
        viewportId,
      }));

      const { synchronizers, SynchronizerManager } = tools;
      let sync = SynchronizerManager.getSynchronizer(ZOOM_PAN_SYNC_ID);
      if (enabled) {
        if (!sync) sync = synchronizers.createZoomPanSynchronizer(ZOOM_PAN_SYNC_ID);
        // Snapshot before mutating — remove() splices the backing arrays.
        const existing = [...sync.getTargetViewports(), ...sync.getSourceViewports()];
        for (const t of existing) sync.remove(t);
        for (const id of ids) sync.add(id);
      } else if (sync) {
        const existing = [...sync.getTargetViewports(), ...sync.getSourceViewports()];
        for (const t of existing) sync.remove(t);
      }
    };

    apply();

    let onEnabled: ((e: Event) => void) | null = null;
    let onDisabled: ((e: Event) => void) | null = null;
    try {
      const cs = getCornerstone();
      onEnabled = () => apply();
      onDisabled = () => apply();
      cs.eventTarget.addEventListener(cs.Enums.Events.ELEMENT_ENABLED, onEnabled);
      cs.eventTarget.addEventListener(cs.Enums.Events.ELEMENT_DISABLED, onDisabled);
    } catch (err) {
      logger.debug("[viewportSync] could not attach element listeners:", err);
    }

    return () => {
      disposed = true;
      try {
        const cs = getCornerstone();
        if (onEnabled)
          cs.eventTarget.removeEventListener(cs.Enums.Events.ELEMENT_ENABLED, onEnabled);
        if (onDisabled)
          cs.eventTarget.removeEventListener(cs.Enums.Events.ELEMENT_DISABLED, onDisabled);
      } catch (err) {
        logger.debug("[viewportSync] cleanup listener removal failed:", err);
      }
    };
  }, [enabled, slotPrefixes]);
}

/**
 * Per-series world-Z axis: the signed world position (mm along the slice
 * normal) of every slice, list-index ordered to match `slotState.sliceIndex`,
 * plus the series' frame of reference. Built from study metadata — never from
 * cornerstone `getImageData`, whose shape differs between stack and volume
 * viewports — so slice sync behaves identically for both. Instances arrive in
 * the same list order `buildS3ImageIds` produces, so `worldZ[sliceIndex]` is
 * the position of the slice the slot atom currently shows.
 */
interface SeriesGeometry {
  worldZ: (number | null)[];
  forUID: string | null;
}

/**
 * Slice sync via Jotai store subscriptions. Links each phase to the slice whose
 * world position is nearest the active phase's current slice (see
 * `sliceSyncMath`). Drives targets by writing their slot atoms — the same path
 * `ProtocolViewport` already wires to both `StackViewport` (`setImageIdIndex`)
 * and `VolumeViewport` (`jumpToSlice`), so it is render-type agnostic. The hook
 * never touches cornerstone directly for slice sync.
 */
function useSliceSync(
  enabled: boolean,
  syncedSlots: readonly SlotId[],
  bottomRightIsSegPanel: boolean,
  studyMetadata: StudyMetadataResponse | undefined,
): void {
  const store = useStore();

  // Ref-mirror so the broadcast loop sees the current value without tearing
  // down the effect each time the annotation toggle flips. (#389) Updated at
  // render rather than in a useEffect so there's no staleness window between
  // commit and effect fire.
  const segPanelRef = useRef(bottomRightIsSegPanel);
  segPanelRef.current = bottomRightIsSegPanel;

  // World-Z axis per series, keyed by seriesId. Pure and cheap (a handful of
  // series × ≤~1k slices); rebuilt only when the study changes — volumes don't
  // mutate mid-session.
  const seriesGeom = useMemo(() => {
    const map = new Map<string, SeriesGeometry>();
    if (!studyMetadata) return map;
    for (const [seriesId, instances] of Object.entries(studyMetadata.series)) {
      const meta = studyMetadata.series_metadata[seriesId];
      const normal = sliceNormalFromIop(meta?.image_orientation_patient);
      const worldZ = instances.map((inst) =>
        instanceWorldZ(inst.image_position_patient, inst.z_position, normal),
      );
      map.set(seriesId, { worldZ, forUID: meta?.frame_of_reference_uid ?? null });
    }
    return map;
  }, [studyMetadata]);

  useEffect(() => {
    if (!enabled) return;

    // Last `sliceIndex` observed per slot. Seed from current state so the first
    // scroll, not the initial render, triggers a broadcast.
    const last = new Map<SlotId, number>();
    for (const slot of syncedSlots) {
      last.set(slot, store.get(slotStateFamily(slot)).sliceIndex);
    }

    // Echo suppression by identity: each broadcast updates `last.set(target,…)`
    // BEFORE `store.set(target,…)`, so when the target's own subscription fires
    // the `state.sliceIndex === last.get(slot)` early-return catches it. This
    // doesn't depend on Jotai's synchronous-dispatch contract — `last` is
    // updated before any dispatch can observe the changed atom.
    const unsubs = syncedSlots.map((slot) =>
      store.sub(slotStateFamily(slot), () => {
        const state = store.get(slotStateFamily(slot));
        // Only react to slice changes, not windowing/zoom/etc. Also catches
        // echoes from our own broadcast (we update `last` before setting).
        if (state.sliceIndex === last.get(slot)) return;
        last.set(slot, state.sliceIndex);

        // Only broadcast from the active slot — the one the user is actually
        // wheel-scrolling or sliding. Cornerstone's reference-lines tool (on by
        // default) scrolls companion viewports when the user *clicks* the
        // active viewport; without this gate, those reference-lines-induced
        // scrolls re-broadcast through us and jump the active viewport off the
        // user's lesion mid-draw.
        if (slot !== store.get(activeSlotAtom)) return;

        if (!state.seriesId) return;
        const source = seriesGeom.get(state.seriesId);
        if (!source || source.worldZ.length <= 1) return; // nothing to project from
        const sourceZ = source.worldZ[state.sliceIndex];
        if (sourceZ == null) {
          logger.debug(
            `[viewportSync] source ${slot} slice ${state.sliceIndex} has no world position; skipping broadcast`,
          );
          return;
        }

        for (const other of syncedSlots) {
          if (other === slot) continue;
          // Bottom-right is the SegmentationViewerPanel (no cornerstone
          // viewport) when phaseCount ≤ 3 or any annotation is active —
          // driving its atom would be a no-op write. (#389)
          if (other === "bottom-right" && segPanelRef.current) continue;
          const otherState = store.get(slotStateFamily(other));
          if (!otherState.seriesId) continue;
          const target = seriesGeom.get(otherState.seriesId);
          if (!target) continue;

          // Only link phases proven to share a coordinate system. Different
          // frames of reference make world-Z incomparable, so we leave the pane
          // unsynced rather than mis-register anatomy (laterality-grade risk).
          // Absent FoR on either side → we can't prove a mismatch, so we
          // proceed, matching the pre-world-Z behavior.
          if (source.forUID && target.forUID && source.forUID !== target.forUID) {
            logger.debug(
              `[viewportSync] frame-of-reference mismatch ${slot}→${other}; leaving unsynced`,
            );
            continue;
          }

          // Nearest target slice by world position. Out-of-coverage (e.g. the
          // caudal end a shorter phase doesn't reach) parks at that phase's
          // nearest edge slice rather than skipping — "always link to nearest".
          const mapped = nearestSliceByWorldZ(target.worldZ, sourceZ);
          if (mapped == null) continue;

          if (mapped !== otherState.sliceIndex) {
            // Update `last` before the atom so the target's subscription sees
            // an equal value and short-circuits (echo suppression).
            last.set(other, mapped);
            store.set(slotStateFamily(other), { ...otherState, sliceIndex: mapped });
          }
        }
      }),
    );

    return () => {
      for (const u of unsubs) u();
    };
  }, [enabled, store, syncedSlots, seriesGeom]);
}

export function useHangingProtocolSync(studyMetadata: StudyMetadataResponse | undefined): void {
  const gatingMode = useAtomValue(projectGatingModeAtom);
  const sliceSyncEnabled = useAtomValue(sliceSyncEnabledAtom);
  const manipulationSyncEnabled = useAtomValue(manipulationSyncEnabledAtom);
  const availableDiscriminators = useAtomValue(availableDiscriminatorsAtom);
  const isAnnotationActive = useAtomValue(isSegmentationActiveAtom);

  // Sync is only meaningful when each viewport holds a different phase of the
  // same study. For view_dependent (us3d) or non-gated projects the slots hold
  // unrelated series, so the toggles would crosslink scrolls and force every
  // viewport back to slice 0 on series swap.
  const active = gatingMode === GATING_MODE.PHASE_DEPENDENT;

  // Bottom-right hosts SegmentationViewerPanel (not a cornerstone viewport)
  // when phaseCount ≤ 3, or when an annotation is active for any phaseCount —
  // mirror of `renderBottomRightAsSegPanel` in HangingProtocolGrid. The slice
  // sync skips broadcasting to this slot when it's a seg panel; zoom/pan sync
  // self-filters at the viewportId-prefix level (no cornerstone viewport with
  // a `hangingProtocol_bottom-right` id exists when the panel is up). Keeping
  // `syncedSlots` stable across the annotation toggle avoids tearing down and
  // rebuilding both sync effects on every Add Annotation / commit. (#389)
  const phaseCount = availableDiscriminators.filter((d) => d.kind === "phase").length;
  const bottomRightIsSegPanel = phaseCount <= 3 || isAnnotationActive;

  useSliceSync(active && sliceSyncEnabled, SLOT_IDS, bottomRightIsSegPanel, studyMetadata);
  useZoomPanSync(active && manipulationSyncEnabled, SLOT_IDS);
}
